import { apiGet } from './client';

const STATIC_CATEGORIES_URL = '/data/categories.json';
const STATIC_PRODUCTS_URL = '/data/products.json';

let staticCategoriesPromise = null;
let staticProductsPromise = null;

async function loadStaticJson(url) {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Static catalog missing (${response.status})`);
  }

  const payload = await response.json();
  return payload?.data ?? payload;
}

function getStaticCategories() {
  if (!staticCategoriesPromise) {
    staticCategoriesPromise = loadStaticJson(STATIC_CATEGORIES_URL).catch((error) => {
      staticCategoriesPromise = null;
      throw error;
    });
  }
  return staticCategoriesPromise;
}

function getStaticProducts() {
  if (!staticProductsPromise) {
    staticProductsPromise = loadStaticJson(STATIC_PRODUCTS_URL).catch((error) => {
      staticProductsPromise = null;
      throw error;
    });
  }
  return staticProductsPromise;
}

function filterProducts(products, params = {}) {
  let items = Array.isArray(products) ? products.slice() : [];

  if (params.category_slug) {
    const slug = String(params.category_slug).toLowerCase();
    items = items.filter((product) => product?.category?.slug === slug);
  }

  if (params.category_id) {
    const id = Number(params.category_id);
    items = items.filter((product) => Number(product?.category?.id) === id);
  }

  if (params.q) {
    const query = String(params.q).toLowerCase();
    items = items.filter((product) => {
      const haystack = `${product?.name || ''} ${product?.sku || ''} ${product?.description || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }

  return items;
}

/**
 * Prefer same-origin static catalog in the browser.
 * Live manage API is attempted first only when explicitly requested,
 * otherwise static data avoids cross-subdomain NetworkError.
 */
async function loadCatalog(liveCall, staticCall, { preferLive = false } = {}) {
  if (!preferLive) {
    try {
      return await staticCall();
    } catch (staticError) {
      try {
        return await liveCall();
      } catch {
        throw staticError;
      }
    }
  }

  try {
    return await liveCall();
  } catch (liveError) {
    try {
      return await staticCall();
    } catch {
      throw liveError;
    }
  }
}

export function fetchCategories({ includeProducts = false } = {}) {
  return loadCatalog(
    () => {
      const params = includeProducts ? { include_products: 1 } : {};
      return apiGet('/categories', params);
    },
    async () => {
      const categories = await getStaticCategories();
      if (!includeProducts) return categories;

      const products = await getStaticProducts();
      return categories.map((category) => ({
        ...category,
        products: products.filter((product) => product?.category?.id === category.id),
      }));
    },
  );
}

export function fetchProduct(slugOrId) {
  return loadCatalog(
    () => apiGet(`/products/${encodeURIComponent(slugOrId)}`),
    async () => {
      const products = await getStaticProducts();
      const key = String(slugOrId);
      const match = products.find(
        (product) => String(product.id) === key || product.slug === key,
      );
      if (!match) {
        throw new Error('Product not found.');
      }
      return match;
    },
  );
}

export async function fetchProductsPage(params = {}) {
  return loadCatalog(
    () => apiGet('/products', params),
    async () => {
      const products = filterProducts(await getStaticProducts(), params);
      const page = Number(params.page) || 1;
      const perPage = Number(params.per_page) || products.length || 100;
      const start = (page - 1) * perPage;
      const data = products.slice(start, start + perPage);
      const lastPage = Math.max(1, Math.ceil(products.length / perPage));

      return {
        current_page: page,
        data,
        last_page: lastPage,
        per_page: perPage,
        total: products.length,
      };
    },
  );
}

export async function fetchAllProducts(params = {}) {
  // Static catalog already contains all products — avoid multi-page live calls.
  try {
    return filterProducts(await getStaticProducts(), params);
  } catch (staticError) {
    const perPage = 100;
    let page = 1;
    let items = [];
    let lastPage = 1;

    try {
      do {
        const paginator = await apiGet('/products', { ...params, per_page: perPage, page });
        items = items.concat(paginator.data || []);
        lastPage = paginator.last_page || 1;
        page += 1;
      } while (page <= lastPage);
      return items;
    } catch {
      throw staticError;
    }
  }
}
